"use client";

import { useEffect, useRef, type CSSProperties } from "react";

interface FuzzballSphereProps {
  /** Rendered square size in CSS pixels. Omit when using `fill`. */
  size?: number;
  /** Fill the parent element instead of using a fixed size (parent sets the box). */
  fill?: boolean;
  /** 0..1 progress; the sphere eases to a faster spin as this rises. */
  load?: number;
  /** Overlay two counter-rotating orbiting-arc rings around the cluster. */
  arcs?: boolean;
  /** Number of orbiting particles. Fewer reads cleaner at small sizes. */
  points?: number;
  /** Base dot/link color and the near-side highlight color. */
  base?: string;
  highlight?: string;
  className?: string;
}

/**
 * Orbiting particle sphere — ported from ctrliq/fuzzball-dash
 * (startFuzzballSphere), recolored to the app's purple accent and tuned for
 * small decorative use. Pure <canvas> + requestAnimationFrame, no deps.
 */
export default function FuzzballSphere({
  size = 28,
  fill = false,
  load = 0,
  arcs = false,
  points = 170,
  base = "#9333ea",      // purple-600
  highlight = "#c084fc", // purple-400
  className,
}: FuzzballSphereProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Track load in a ref so changing it speeds up the existing animation
  // without tearing down and rebuilding the particle cloud.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Respect reduced-motion: draw a single static frame, no animation loop.
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    const N = points;
    const pts = Array.from({ length: N }, (_, i) => {
      const y = 1 - (i / (N - 1)) * 2;
      const rad = Math.sqrt(1 - y * y);
      const th = i * 2.39996; // golden-angle spiral
      return {
        x: Math.cos(th) * rad, y, z: Math.sin(th) * rad,
        f: 0.85 + Math.random() * 0.3, ph: Math.random() * Math.PI * 2,
        w1: 0.5 + Math.random() * 1.8, w2: 0.4 + Math.random() * 1.5, w3: 0.3 + Math.random() * 1.2,
        a1: 0.03 + Math.random() * 0.09, a2: 0.03 + Math.random() * 0.09, a3: 0.03 + Math.random() * 0.09,
        p1: Math.random() * 6.28, p2: Math.random() * 6.28, p3: Math.random() * 6.28,
        spin: 0.75 + Math.random() * 0.5,
      };
    });

    // Link near neighbours. Threshold scales with density (orig: .02 @ N=700).
    const links: [number, number][] = [];
    const thr = 14 / N;
    for (let i = 0; i < N; i++) for (let k = i + 1; k < N; k++) {
      const a = pts[i], b = pts[k];
      const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
      if (dx * dx + dy * dy + dz * dz < thr) links.push([i, k]);
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0, sim = 0, lastReal = performance.now() / 1000;
    let speed = 0.4;                        // eased spin rate
    const MIN_SPEED = 0.4, MAX_SPEED = 2.0; // idle → fully-gathered

    const draw = () => {
      // Measure the rendered element so `fill` (or any CSS size) works and the
      // backing buffer stays crisp on HiDPI displays.
      const w = canvas.clientWidth, h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      const bw = Math.round(w * dpr), bh = Math.round(h * dpr);
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw; canvas.height = bh;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const real = performance.now() / 1000;
      const target = MIN_SPEED + Math.max(0, Math.min(1, loadRef.current)) * (MAX_SPEED - MIN_SPEED);
      speed += (target - speed) * 0.04;   // ease toward the load-driven rate
      sim += (real - lastReal) * speed;
      lastReal = real;
      const now = sim;
      const rx = Math.sin(now * 0.1) * 0.35;
      const cx2 = Math.cos(rx), sx2 = Math.sin(rx);
      const R = Math.min(w, h) * 0.42, ox = w / 2, oy = h / 2;
      const proj = pts.map(p => {
        const wob = 1 + 0.05 * Math.sin(now * 1.3 + p.ph);
        const r = p.f * wob;
        const x = p.x * r + Math.sin(now * p.w1 + p.p1) * p.a1;
        const y = p.y * r + Math.sin(now * p.w2 + p.p2) * p.a2;
        const z = p.z * r + Math.sin(now * p.w3 + p.p3) * p.a3;
        const ry = now * 0.25 * p.spin;
        const cy = Math.cos(ry), sy = Math.sin(ry);
        let x2 = x * cy + z * sy; const z2 = -x * sy + z * cy;
        let y2 = y * cx2 - z2 * sx2; const z3 = y * sx2 + z2 * cx2;
        x2 += Math.sin(now * p.w2 + p.p3) * p.a2 + Math.sin(now * 2.7 * p.w3 + p.p1) * p.a3 * 0.5;
        y2 += Math.sin(now * p.w1 + p.p2) * p.a1 + Math.sin(now * 2.3 * p.w2 + p.p1) * p.a2 * 0.5;
        const s = 1.6 / (1.6 + z3 * 0.55);
        return { sx: ox + x2 * R * s, sy: oy + y2 * R * s, z: z3, s };
      });
      ctx.lineWidth = 0.5;
      ctx.strokeStyle = base;
      for (const [i, k] of links) {
        const a = proj[i], b = proj[k];
        const depth = 1 - ((a.z + b.z) / 2 + 1) / 2;
        ctx.globalAlpha = 0.05 + depth * 0.25;
        ctx.beginPath(); ctx.moveTo(a.sx, a.sy); ctx.lineTo(b.sx, b.sy); ctx.stroke();
      }
      for (const p of proj) {
        const depth = 1 - (p.z + 1) / 2;
        ctx.globalAlpha = 0.15 + depth * 0.7;
        ctx.fillStyle = depth > 0.75 ? highlight : base;
        const r = (0.5 + depth * 1.2) * p.s;
        ctx.beginPath(); ctx.arc(p.sx, p.sy, r, 0, 6.284); ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    if (reduce) {
      // Draw one frame after layout settles (clientWidth is 0 before paint).
      raf = requestAnimationFrame(draw);
      return () => cancelAnimationFrame(raf);
    }
    const step = () => {
      if (!canvas.isConnected) { cancelAnimationFrame(raf); return; }
      draw();
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [size, fill, points, base, highlight]);

  const boxStyle: CSSProperties = fill
    ? { position: "relative", width: "100%", height: "100%" }
    : { position: "relative", width: size, height: size };

  return (
    <div className={className} style={boxStyle} aria-hidden="true">
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      />
      {arcs && (
        <>
          <style>{`
            @keyframes fbz-rot { to { transform: rotate(360deg); } }
            @keyframes fbz-rotr { to { transform: rotate(-360deg); } }
            @media (prefers-reduced-motion: reduce) { .fbz-arc { animation: none !important; } }
          `}</style>
          {/* Two 60° arcs, clockwise (fuzzball-dash "qrot"). */}
          <svg
            viewBox="0 0 100 100"
            className="fbz-arc"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", transformOrigin: "center", animation: "fbz-rot 24s linear infinite" }}
          >
            <path d="M 50 4 A 46 46 0 0 1 89.84 27" fill="none" stroke={base} strokeWidth="1.4" strokeLinecap="round" opacity="0.9" />
            <path d="M 50 96 A 46 46 0 0 1 10.16 73" fill="none" stroke={base} strokeWidth="1.4" strokeLinecap="round" opacity="0.4" />
          </svg>
          {/* A short dashed segment orbiting counter-clockwise ("qrotr"). */}
          <svg
            viewBox="0 0 100 100"
            className="fbz-arc"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", transformOrigin: "center", animation: "fbz-rotr 40s linear infinite" }}
          >
            <circle cx="50" cy="50" r="44" fill="none" stroke={highlight} strokeWidth="2" strokeDasharray="8 268" strokeLinecap="round" opacity="0.55" />
          </svg>
        </>
      )}
    </div>
  );
}
